import { nameByPlatform } from "~/constants/platfroms";
import { colorByPlatform } from "~/feature/chart/constants";
import {
  ChartViewEnum,
  type IMarketGroupChanceHistory,
} from "~/feature/chart/types";
import {
  getMarketLineKey,
  isNoSide,
  sidesByViewMode,
} from "~/feature/chart/utils";
import type { IMarket } from "~/types/event";
import type { PlatformEnum } from "~/types/platform";
import { formatPercentageValue } from "~/utils/formatValue";

export interface IMarketLegendItem {
  key: string;
  label: string;
  color: string;
  isDashed: boolean;
  chanceLabel: string | null;
  changeLabel: string | null;
  isPositiveChange: boolean;
}

export interface IConsensusLegend {
  marketsLabel: string;
  platforms: {
    key: string;
    name: string;
    color: string;
    chanceLabel: string | null;
  }[];
  nowLabel: string | null;
  changeLabel: string | null;
  isPositiveChange: boolean;
  spreadLabel: string | null;
  spreadPointsLabel: string | null;
}

const percent = (value: number) =>
  formatPercentageValue(value, { maximumFractionDigits: 1 });

const signed = (value: number) => `${value < 0 ? "" : "+"}${percent(value)}`;

const points = (value: number) => percent(value).replace("%", "");

const NOT_AVAILABLE_LABEL = "N/A";

const label = (value: number | null, isLoading: boolean) => {
  if (value !== null) {
    return percent(value);
  }

  return isLoading ? null : NOT_AVAILABLE_LABEL;
};

const isNumber = (value: number | null): value is number => value !== null;

const getChance = (
  chanceHistory: IMarketGroupChanceHistory[],
  marketId: string,
  isNo = false
) => {
  const chances = chanceHistory
    .flatMap(({ historical }) => historical)
    .flatMap(({ byPlatform }) => byPlatform)
    .filter((entry) => entry.marketId === marketId)
    .map((entry) => (isNo ? entry.noChance : entry.yesChance));

  const first = chances.at(0);
  const now = chances.at(-1);

  return {
    now: now ?? null,
    change: first === undefined || now === undefined ? null : now - first,
  };
};

const getSelected = (markets: IMarket[], selections: string[]) =>
  markets.filter(({ id }) => selections.includes(id));

export const buildMarketItems = (
  markets: IMarket[],
  selections: string[],
  chanceHistory: IMarketGroupChanceHistory[],
  viewMode: ChartViewEnum,
  isLoading: boolean
): IMarketLegendItem[] => {
  const sides = sidesByViewMode[viewMode];

  return getSelected(markets, selections).flatMap((market) =>
    sides.map((side) => {
      const isNo = isNoSide(side);
      const { now, change } = getChance(chanceHistory, market.id, isNo);
      const platform = market.platform as PlatformEnum;
      const suffix = sides.length === 1 ? "" : isNo ? " No" : " Yes";

      return {
        key: getMarketLineKey(market.id, side),
        label: `${market.title} - ${nameByPlatform[platform]}${suffix}`,
        color: colorByPlatform[platform],
        isDashed: isNo,
        chanceLabel: label(now, isLoading),
        changeLabel: change === null ? null : signed(change),
        isPositiveChange: (change ?? 0) >= 0,
      };
    })
  );
};

export const buildConsensusLegend = (
  markets: IMarket[],
  selections: string[],
  chanceHistory: IMarketGroupChanceHistory[],
  isLoading: boolean
): IConsensusLegend => {
  const platforms = getSelected(markets, selections).map((market) => {
    const platform = market.platform as PlatformEnum;

    return {
      key: market.id,
      name: nameByPlatform[platform],
      color: colorByPlatform[platform],
      ...getChance(chanceHistory, market.id),
    };
  });

  const nows = platforms.map(({ now }) => now).filter(isNumber);

  const consensusPoints = chanceHistory.flatMap(({ historical }) => historical);
  const consensusFirst = consensusPoints.at(0)?.consensus?.yesChance ?? null;
  const consensusNow = consensusPoints.at(-1)?.consensus?.yesChance ?? null;
  // The move of the consensus line itself, so the chip agrees with the line.
  const averageChange =
    consensusFirst === null || consensusNow === null
      ? null
      : consensusNow - consensusFirst;

  return {
    marketsLabel: `${platforms.length} market${platforms.length === 1 ? "" : "s"}`,
    platforms: platforms.map(({ key, name, color, now }) => ({
      key,
      name,
      color,
      chanceLabel: label(now, isLoading),
    })),
    nowLabel: label(consensusNow, isLoading),
    changeLabel: averageChange === null ? null : signed(averageChange),
    isPositiveChange: (averageChange ?? 0) >= 0,
    spreadLabel: nows.length
      ? `${points(Math.min(...nows))}-${percent(Math.max(...nows))}`
      : null,
    spreadPointsLabel: nows.length
      ? `${points(Math.max(...nows) - Math.min(...nows))}pp`
      : null,
  };
};
